import { SearchIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputLeftElement,
    Radio,
    RadioGroup,
    Stack,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import ConfirmDialog from '../../../components/ConfirmDialog';
import FormModal from '../../../components/FormModal';
import PageCard from '../../../components/page-card';
import { translate } from '../../../i18n';
import {
    MembershipSubscriptionDto,
    SubscriptionStatus,
} from '../../../models/user-membership/dto/user-membership.dto';
import { dateToString } from '../../../utils/date';
import { matchesNameQuery } from '../../../utils/name-search';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useGetUsers } from '../hooks/useGetUsers';
import { useGetUserSubscriptions } from '../hooks/useGetUserSubscriptions';
import {
    useRemoveAttachedUser,
    useSetAttachedUser,
} from '../hooks/useSetAttachedUser';

/**
 * MYS-117 Duo — fiche adhérent panel for a payer's Duo subscription(s):
 * « Rattaché : Amine » with attach / change / detach, the past_due grace
 * badge (« les 2 joueurs concernés »), and the admin throttle override.
 *
 * Rendered only when the user has at least one subscription whose plan has
 * maxAttachedMembers >= 1.
 *
 * Subscriptions come from the PER-USER `users/:userId/subscriptions` read.
 * They used to come from the org-wide users-with-memberships roster on the
 * assumption that it was "already used by the roster — cache-warm"; that only
 * held when an operator navigated here from `/subscribed-users`. Reaching a
 * member any other way (deep link, refresh, a booking or game, search) found
 * a cold cache and pulled the whole organization to render one card — which
 * for most members, and for every organization with no Duo plan at all,
 * renders nothing. Sentry DASHBOARD-17.
 */
export default function DuoAttachmentCard({ userId }: { userId: string }) {
    const { data: subscriptions } = useGetUserSubscriptions(userId);

    const duoSubscriptions = useMemo(() => {
        return (subscriptions ?? []).filter(
            (s) => (s.membershipType.maxAttachedMembers ?? 0) >= 1 && s.status !== SubscriptionStatus.CANCELED,
        );
    }, [subscriptions]);

    if (duoSubscriptions.length === 0) return null;

    return (
        <PageCard title={translate('userMembership.duo_section_title')}>
            <Stack spacing={4}>
                {duoSubscriptions.map((subscription) => (
                    <DuoSubscriptionRow
                        key={subscription.id}
                        subscription={subscription}
                    />
                ))}
            </Stack>
        </PageCard>
    );
}

function DuoSubscriptionRow({
    subscription,
}: {
    subscription: MembershipSubscriptionDto;
}) {
    const { selectedOrganization } = useSelectedOrganization();
    const navigateToUserDetails = useNavigateToUserDetails();
    const attachModal = useDisclosure();
    const detachConfirm = useDisclosure();
    const toast = useToast();
    const { mutateAsync: removeAttachedUser, isLoading: isDetaching } =
        useRemoveAttachedUser(selectedOrganization?.id);

    const attached = subscription.attachedUser;
    const attachedName = attached
        ? `${attached.firstName ?? ''} ${attached.lastName ?? ''}`.trim()
        : '';
    const isInGrace =
        subscription.status === SubscriptionStatus.PAST_DUE &&
        new Date(subscription.currentPeriodEnd) > new Date();

    const handleDetach = async () => {
        try {
            await removeAttachedUser({ subscriptionId: subscription.id });
            detachConfirm.onClose();
        } catch (error: any) {
            toast({
                title: translate('common.error'),
                description: error?.message ?? '',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <Box borderWidth="1px" borderRadius="md" p={4}>
            <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
                <Box>
                    <Flex align="center" gap={2}>
                        <Text fontWeight="700">
                            {subscription.membershipType.name}
                        </Text>
                        <Badge colorScheme="brand" variant="subtle">
                            {translate('userMembership.duo_badge_payer')}
                        </Badge>
                        {isInGrace && (
                            <Badge colorScheme="orange" variant="subtle">
                                {translate('userMembership.duo_grace_badge', {
                                    date: dateToString(
                                        new Date(subscription.currentPeriodEnd),
                                    ),
                                })}
                            </Badge>
                        )}
                    </Flex>
                    {attached ? (
                        <Text mt={1} fontSize="sm">
                            {translate('userMembership.duo_attached')}{' '}
                            <Text
                                as="span"
                                fontWeight="600"
                                color="brand.500"
                                cursor="pointer"
                                onClick={() => navigateToUserDetails?.(attached.id)}
                            >
                                {attachedName}
                            </Text>
                            {subscription.attachedByUserId ? null : subscription.attachedAt ? (
                                // attachedBy null = an operator did it (F18).
                                <Text as="span" color="gray.500" fontSize="xs" ml={2}>
                                    {translate('userMembership.duo_changed_by_club', {
                                        date: dateToString(new Date(subscription.attachedAt)),
                                    })}
                                </Text>
                            ) : null}
                        </Text>
                    ) : (
                        // Empty seat = actionable prompt, not a passive label (F9).
                        <Text mt={1} fontSize="sm" color="gray.500">
                            {translate('userMembership.duo_no_attached')}
                        </Text>
                    )}
                </Box>
                <Flex gap={2}>
                    {attached ? (
                        <>
                            <Button size="sm" variant="outline" onClick={attachModal.onOpen}>
                                {translate('userMembership.duo_change')}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                onClick={detachConfirm.onOpen}
                            >
                                {translate('userMembership.duo_remove')}
                            </Button>
                        </>
                    ) : (
                        <Button size="sm" colorScheme="brand" onClick={attachModal.onOpen}>
                            {translate('userMembership.duo_attach_cta')}
                        </Button>
                    )}
                </Flex>
            </Flex>

            <AttachedPlayerModal
                subscription={subscription}
                isOpen={attachModal.isOpen}
                onClose={attachModal.onClose}
            />
            <ConfirmDialog
                isOpen={detachConfirm.isOpen}
                onClose={detachConfirm.onClose}
                onConfirm={handleDetach}
                title={translate('userMembership.duo_detach_confirm_title')}
                body={translate('userMembership.duo_detach_confirm_body', {
                    name: attachedName,
                })}
                confirmLabel={translate('userMembership.duo_remove')}
                cancelLabel={translate('common.cancel')}
                isConfirming={isDetaching}
            />
        </Box>
    );
}

/**
 * Single-select org-player picker (cribbed from AssignCouponModal), plus the
 * throttle-override checkbox. API error codes map to human copy (F8):
 * ALREADY_COVERED renders inline; ATTACH_CHANGE_LIMIT surfaces the override.
 */
export function AttachedPlayerModal({
    subscription,
    isOpen,
    onClose,
}: {
    subscription: MembershipSubscriptionDto;
    isOpen: boolean;
    onClose: () => void;
}) {
    const { selectedOrganization } = useSelectedOrganization();
    // Gated on `isOpen` — one of these is mounted per Duo subscription row on
    // the member page, so viewing a member pulled the whole roster once per row
    // (DASHBOARD-Z).
    // R92-5 — `isError` is taken. Post-R89-1 a refused `GET /users` leaves
    // `data === undefined`, `filteredUsers` falls to `[]`, and this modal —
    // opened to attach a second player to a paid Duo subscription — told the
    // operator there was nobody in the organization to attach.
    //
    // R116-2 — taking `isError` covered the REFUSED read and left the
    // NEVER-ISSUED one saying the same wrong sentence. The hook's gate is
    // `Boolean(organizationId) && (options?.enabled ?? true)`: two terms, and
    // only the second is supplied here. The first is reachably unsatisfied,
    // because nothing on the path to this modal needs an organization —
    // `useGetUserSubscriptions` is gated on `userId` alone, so the card mounts,
    // the Duo row renders, and "Attach" opens — and a refused or empty
    // `my-organizations` leaves nothing selected for the whole session (run
    // 100). The modal then showed "No users found" about a roster it never
    // asked for, on the flow that seats the second player of a subscription
    // somebody is paying for.
    //
    // `classifyRead` rather than a private truthiness test (D59). Note it also
    // buys the in-flight arm, which this modal never had: until the roster
    // arrives, `filteredUsers` is `[]` and the same "No users found" was shown
    // to every operator for the length of the largest read the dashboard makes
    // (the whole org roster, unpaginated — Sentry DASHBOARD-Z).
    const {
        data: users,
        isError: isUsersError,
        isInitialLoading: isUsersInitialLoading,
    } = useGetUsers(selectedOrganization?.id, undefined, {
        enabled: isOpen,
    });
    const usersReadState = classifyRead(isUsersError, isUsersInitialLoading, users);
    const { mutateAsync: setAttachedUser, isLoading } = useSetAttachedUser(
        selectedOrganization?.id,
    );
    const toast = useToast();

    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [overrideThrottle, setOverrideThrottle] = useState(false);
    const [inlineError, setInlineError] = useState<string | null>(null);

    const filteredUsers = useMemo(() => {
        if (!users) return [];
        const eligible = users.filter((u) => u.id !== subscription.userId);
        if (!searchQuery) return eligible;
        return eligible.filter((user) => matchesNameQuery(searchQuery, user));
    }, [users, searchQuery, subscription.userId]);

    const onModalClose = () => {
        setSelectedUserId('');
        setSearchQuery('');
        setOverrideThrottle(false);
        setInlineError(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedUserId) return;
        setInlineError(null);
        try {
            await setAttachedUser({
                subscriptionId: subscription.id,
                body: { attachedUserId: selectedUserId, overrideThrottle },
            });
            toast({
                title: translate('userMembership.duo_attach_success'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onModalClose();
        } catch (error: any) {
            const code = error?.code ?? error?.body?.code;
            if (code === 'ALREADY_COVERED') {
                setInlineError(translate('userMembership.duo_already_covered'));
            } else if (code === 'ATTACH_CHANGE_LIMIT') {
                setInlineError(translate('userMembership.duo_throttle_error'));
            } else {
                toast({
                    title: translate('common.error'),
                    description: error?.message ?? '',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={onModalClose}
            title={translate('userMembership.duo_attach_modal_title', {
                name: subscription.membershipType.name,
            })}
            size="xl"
            onSubmit={handleSubmit}
            submitLabel={translate('userMembership.duo_attach_cta')}
            cancelLabel={translate('common.cancel')}
            isSubmitting={isLoading}
            isSubmitDisabled={!selectedUserId}
        >
            <FormControl mb={4}>
                <FormLabel>{translate('coupon.search_users')}</FormLabel>
                <InputGroup>
                    <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder={translate('coupon.search_by_name_or_email')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </InputGroup>
            </FormControl>

            <RadioGroup value={selectedUserId} onChange={setSelectedUserId}>
                <Stack spacing={2} maxH="360px" overflowY="auto">
                    {filteredUsers.map((user) => (
                        <Radio key={user.id} value={user.id}>
                            <Text>
                                {user.firstName} {user.lastName} ({user.email})
                            </Text>
                        </Radio>
                    ))}
                    {filteredUsers.length === 0 && (
                        // Empty state echoes the query (design system rule) —
                        // but only when there IS a roster to have searched. The
                        // other three read states each get their own sentence:
                        // a refused read is a failure (R92-5), a read still in
                        // flight is not an answer, and a read that was never
                        // issued is a missing organization, which the navbar
                        // picker fixes and nothing in this dialog can. A
                        // `ScopePrompt` panel is deliberately NOT used — this is
                        // one control inside a form, the same call run 114
                        // settled on for `AddUserToGameModal`.
                        <Text
                            color={
                                usersReadState === 'unreadable'
                                    ? 'danger.500'
                                    : 'gray.500'
                            }
                            textAlign="center"
                            py={8}
                        >
                            {usersReadState === 'unreadable'
                                ? translate('user.error_loading')
                                : usersReadState === 'loading'
                                  ? translate('cohort.loading')
                                  : usersReadState === 'never-issued'
                                    ? translate(
                                          scopePromptI18nKey('organization') as any,
                                      )
                                    : searchQuery
                                      ? translate('userMembership.duo_search_empty', {
                                            query: searchQuery,
                                        })
                                      : translate('cohort.no_users_found')}
                        </Text>
                    )}
                </Stack>
            </RadioGroup>

            {inlineError && (
                <Text color="red.500" fontSize="sm" mt={3}>
                    {inlineError}
                </Text>
            )}

            {/* Throttle override — admin capability, with the F18 warning. */}
            <Checkbox
                mt={4}
                isChecked={overrideThrottle}
                onChange={(e) => setOverrideThrottle(e.target.checked)}
            >
                <Text fontSize="sm">
                    {translate('userMembership.duo_throttle_override_label')}
                </Text>
            </Checkbox>
        </FormModal>
    );
}
